'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { usePathname, useRouter } from 'next/navigation';

import Breadcrumb from '@/components/breadcrumbs';
import Cart from '@/components/cart';
import CheckoutStep from '@/components/checkout/checkoutStep';
import EstimateShippingModal from '@/components/estimateShippingModal';
import Heading from '@/components/heading';
import Loader from '@/components/loader';
import Modal from '@/components/modal';
import StylizedButtonLink from '@/components/stylizedButtonLink';

import { CART_CHANGE_ADDRESS_PARAM } from '@/lib/constants/common';
import {
  orderHasShippingEstimate,
  usePartyCheckoutOrder,
} from '@/lib/hooks/usePartyCheckoutOrder';
import { strings } from '@/lib/data/site/strings';

import cartStyles from '@/components/cart/cart.module.scss';

import type { OrderPayload } from '@/lib/types/orderPayload';

type CheckoutView = 'cart' | 'checkout';

interface OrderCheckoutFlowProps {
  initialView?: CheckoutView;
}

export default function OrderCheckoutFlow({
  initialView = 'cart',
}: OrderCheckoutFlowProps) {
  const router = useRouter();
  const pathname = usePathname();
  const {
    mounted,
    orderData,
    setOrderData,
    loadingCart,
    syncCartOnce,
    orderState,
  } = usePartyCheckoutOrder();

  const [view, setView] = useState<CheckoutView>(initialView);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [proceedToCheckoutAfterModal, setProceedToCheckoutAfterModal] = useState(false);
  const [prefillProfileAddress, setPrefillProfileAddress] = useState(false);
  const handledAddressQueryRef = useRef(false);
  const promptedCheckoutAddressRef = useRef(false);

  const openAddressModal = useCallback((proceedAfterSuccess = false, prefillFromProfile = false) => {
    setProceedToCheckoutAfterModal(proceedAfterSuccess);
    setPrefillProfileAddress(prefillFromProfile);
    setIsAddressModalOpen(true);
  }, []);

  const closeAddressModal = useCallback(() => {
    setProceedToCheckoutAfterModal(false);
    setIsAddressModalOpen(false);
  }, []);

  const handleEstimateSuccess = useCallback(async (order?: OrderPayload | null) => {
    const shouldProceedToCheckout = proceedToCheckoutAfterModal;
    setProceedToCheckoutAfterModal(false);
    setIsAddressModalOpen(false);

    if (order) {
      setOrderData(order);
    } else {
      await syncCartOnce(false);
    }

    if (shouldProceedToCheckout) {
      setView('checkout');
    }
  }, [proceedToCheckoutAfterModal, setOrderData, syncCartOnce]);

  const showCheckout = useCallback(() => {
    setView('checkout');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const showCart = useCallback(() => {
    setView('cart');
    promptedCheckoutAddressRef.current = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!mounted || loadingCart || !orderData || handledAddressQueryRef.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get(CART_CHANGE_ADDRESS_PARAM) !== '1') {
      return;
    }

    handledAddressQueryRef.current = true;
    openAddressModal(view === 'checkout', false);
    router.replace(pathname, { scroll: false });
  }, [loadingCart, mounted, openAddressModal, orderData, pathname, router, view]);

  useEffect(() => {
    if (view !== 'checkout' || loadingCart || !orderData || promptedCheckoutAddressRef.current) {
      return;
    }

    if (!orderHasShippingEstimate(orderData)) {
      promptedCheckoutAddressRef.current = true;
      openAddressModal(true, true);
    }
  }, [loadingCart, openAddressModal, orderData, view]);

  const breadcrumbItems = view === 'cart'
    ? [{ label: 'Cart', href: '/cart' }]
    : [{ label: 'Checkout', href: '/checkout' }];

  const addressModal = (
    <Modal
      isOpen={isAddressModalOpen}
      onClose={closeAddressModal}
      wrapperClass={cartStyles.estimateShippingWrapper}
      modalClass={classNames(cartStyles.estimateShippingModal)}
      content={
        <EstimateShippingModal
          onClose={closeAddressModal}
          onSuccess={handleEstimateSuccess}
          prefillFromProfile={prefillProfileAddress}
        />
      }
    />
  );

  if (!mounted) {
    return null;
  }

  if (loadingCart && orderState?.token) {
    return (
      <>
        <Breadcrumb items={breadcrumbItems} />
        <Heading variant="h1" fontSize="medium">
          {view === 'cart' ? 'My Cart' : 'Checkout'}
        </Heading>
        <Loader variant="2" message="Loading your cart, please wait…" />
      </>
    );
  }

  const cartItems = orderData?.items ?? [];
  if (cartItems.length === 0 || !orderState?.token) {
    return (
      <>
        <Breadcrumb items={breadcrumbItems} />
        <Heading variant="h1" fontSize="medium">My Cart</Heading>
        <div className={cartStyles.cartEmpty}>
          <p>{`Your ${strings.brandNameShort} cart is currently empty.`}</p>
          <StylizedButtonLink variant="secondary" size="mini" href="/all-products/">
            Return to Shop
          </StylizedButtonLink>
        </div>
      </>
    );
  }

  if (!orderData || typeof orderData.pricing?.total !== 'number') {
    return (
      <>
        <Breadcrumb items={breadcrumbItems} />
        <Heading variant="h1" fontSize="medium">
          {view === 'cart' ? 'My Cart' : 'Checkout'}
        </Heading>
        <Loader variant="2" message="Loading your cart, please wait…" />
      </>
    );
  }

  if (view === 'cart') {
    return (
      <>
        <Breadcrumb items={breadcrumbItems} />
        <Heading variant="h1" fontSize="medium">My Cart</Heading>
        <Cart
          orderData={orderData}
          refreshCart={syncCartOnce}
          setOrderData={setOrderData}
          onProceedToCheckout={showCheckout}
          onOpenAddressModal={(proceedAfterSuccess) => openAddressModal(proceedAfterSuccess, proceedAfterSuccess)}
        />
        {addressModal}
      </>
    );
  }

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />
      <Heading variant="h1" fontSize="medium">Checkout</Heading>
      <CheckoutStep
        orderData={orderData}
        refreshCart={syncCartOnce}
        setOrderData={setOrderData}
        onOpenAddressModal={() => openAddressModal(false)}
        onEditCart={showCart}
      />
      {addressModal}
    </>
  );
}
